import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import { getCoachCareerStats } from '../../actions/coachActions'
import Modal from '../common/Modal'
import Tooltip from '../common/Tooltip'
import TooltipText from '../common/TooltipText'
import Loader from '../common/loader/legacy-loaders/Op36Loader-web'

import classes from './CoachCareerStatsModal.module.scss'

const STAT_KEYS = [
  'level_ups_credited', 'program_created', 'program_completed', 'event_scored',
  'announcement_sent', 'objective_approved', 'activity_validated',
]

const CoachCareerStatsModal = ({ isOpen, onClose, coach }) => {
  const { t } = useTranslation()
  const [careerStats, setCareerStats] = useState(null)

  useEffect(() => {
    if (!isOpen) { return }

    getCoachCareerStats(coach.id).then((response) => setCareerStats(response.data.career_stats))
  }, [isOpen, coach.id])

  const fullName = `${coach.first_name || ''} ${coach.last_name || ''}`.trim()

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={classes.coachCareerStatsModalContent}
      title={t('coaches.careerStats.title', { name: fullName })}
    >
      {careerStats === null ? (
        <div className={classes.loaderWrapper}>
          <Loader message={t('coaches.careerStats.loading')} />
        </div>
      ) : (
        <div className={classes.tableWrapper}>
          <Tooltip offset={{ top: -5 }} />
          <table className={classes.careerStatsTable}>
            <thead>
              <tr>
                <th>{t('coaches.careerStats.community')}</th>
                <th>{t('coaches.careerStats.tenure')}</th>
                {STAT_KEYS.map((statKey) => (
                  <th key={statKey}>{t(`coaches.careerStats.stats.${statKey}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {careerStats.communities.map((communityRow) => (
                <tr key={communityRow.community.id}>
                  <td className={classes.communityName}>
                    <TooltipText text={communityRow.community.name} />
                  </td>
                  <td className={classes.tenureCell}>
                    {communityRow.tenures.length === 0 ? '-' : communityRow.tenures.map((tenure, index) => (
                      <div key={index}>
                        {moment(tenure.started_at).format('MM/DD/YYYY')}
                        {' - '}
                        {tenure.ended_at
                          ? moment(tenure.ended_at).format('MM/DD/YYYY')
                          : <span className={classes.present}>{t('coaches.careerStats.present')}</span>}
                      </div>
                    ))}
                  </td>
                  {STAT_KEYS.map((statKey) => (
                    <td key={statKey}>{communityRow[statKey]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className={classes.totalsRow}>
                <td>{t('coaches.careerStats.allCommunities')}</td>
                <td />
                {STAT_KEYS.map((statKey) => (
                  <td key={statKey}>{careerStats.totals[statKey]}</td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Modal>
  )
}

CoachCareerStatsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  coach: PropTypes.shape({
    id: PropTypes.number.isRequired,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
  }).isRequired,
}

export default CoachCareerStatsModal
